import { EventEmitter, Injectable } from '@angular/core';
import {
  Firestore,
  Timestamp,
  waitForPendingWrites,
  enableNetwork,
  disableNetwork,
  DocumentReference,
  doc,
  docSnapshots,
  CollectionReference,
  collection,
  collectionChanges,
  Query,
  docData,
  collectionData,
  setDoc,
  getDoc,
  getDocs,
  DocumentSnapshot,
  DocumentChange,
  SetOptions,
  FieldValue,
  deleteField,
  getDocFromCache,
  getDocFromServer
} from '@angular/fire/firestore';
import { Observable, firstValueFrom, of } from 'rxjs';
import { map, first, distinctUntilChanged, takeUntil, filter, timeout } from 'rxjs/operators';
import { Auth, authState, idToken, signInWithCustomToken, signOut, User } from '@angular/fire/auth';
import { isEqual } from 'lodash';
import { OnlineOfflineService } from './online-offine.service';

type DocPredicate<T> = string | DocumentReference<T>;
type CollectionPredicate<T> = string | CollectionReference<T>;

@Injectable({
  providedIn: 'root'
})
export class FirestoreService {

  loggedIn: EventEmitter<User> = new EventEmitter();
  loggedOut: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private angularFirestore: Firestore,
    private angularFireAuth: Auth,
    private onlineOfflineService: OnlineOfflineService
  ) { }

  _customToken: string = '';
  async signIn(token: string) {
    this._customToken = token;
    await signInWithCustomToken(this.angularFireAuth, token);
  }

  async signOut() {
    const currentUser = await this.isLoggedIn();
    if (currentUser) {
      signOut(this.angularFireAuth).then(() => this.loggedOut.emit(true));
    }
  }

  isLoggedIn(): Promise<User | null> {
    return firstValueFrom(authState(this.angularFireAuth).pipe(first()));
  }

  /**
   * Resolves once Firebase Auth has established a signed-in user, so an auth-required read is never
   * issued before signInWithCustomToken() has completed. Reading too early returns `permission-denied`,
   * which the settings readers silently collapse into their default value — surfacing as Bug 27328
   * (Sales Order v2.5 toggle shows the default ON instead of the saved value after a reload/deep-link).
   *
   * `first()` makes this a no-op (resolves on the next microtask) once a user is already present, so it
   * adds no latency after login; it only actually waits during the brief sign-in window, and — because
   * Firebase Auth restores its persisted user without a network call — it resolves offline too. If no
   * user appears within `timeoutMs` (a genuinely signed-out caller), it resolves `null` and the read
   * proceeds as before rather than hanging forever.
   */
  async waitForAuthUser(timeoutMs = 8000): Promise<User | null> {
    return firstValueFrom(
      authState(this.angularFireAuth).pipe(
        filter((u): u is User => !!u),
        first(),
        timeout({ first: timeoutMs, with: () => of<User | null>(null) })
      )
    ).catch(() => null);
  }

  getIdToken(): Promise<string | null> {
    return firstValueFrom(idToken(this.angularFireAuth));
  }

  serverDateTime() {
    let dateTimeNow = Timestamp.now().toDate();
    return dateTimeNow ? dateTimeNow : new Date();
  }

  async waitForPendingWrites() {
    return waitForPendingWrites(this.angularFirestore);
  }

  async enableNetwork() {
    enableNetwork(this.angularFirestore);
  }

  async disableNetwork() {
    disableNetwork(this.angularFirestore);
  }

  doc<T>(ref: DocPredicate<T>): DocumentReference<T> {
    return typeof ref === 'string' ? doc(this.angularFirestore, ref) as DocumentReference<T> : ref;
  }

  docSnapshots$<T>(ref: DocPredicate<T>): Observable<T> {
    return docSnapshots<T>(this.doc(ref)).pipe(map((doc: DocumentSnapshot<T>) => {
      return doc.data() as T;
    }))
      .pipe(distinctUntilChanged((prev, curr) => isEqual(prev, curr)), takeUntil(this.loggedOut));
  }

  async getDocSnapshotAsync<T>(ref: DocPredicate<T>): Promise<DocumentSnapshot<T>> {
    // Don't read before Firebase sign-in is established (avoids permission-denied on the reload race).
    await this.waitForAuthUser();
    return await getDoc(this.doc(ref));
  }

  /**
   * The locally cached copy of a document, or `undefined` when the cache has
   * none. Never touches the network, so it answers immediately online or
   * offline — the read side of a compare-then-queue write.
   */
  async getDocFromCacheAsync<T>(ref: DocPredicate<T>): Promise<T | undefined> {
    try {
      const snapshot = await getDocFromCache(this.doc(ref));
      return snapshot.exists() ? snapshot.data() : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * The server's copy of a document, bounded in time and never throwing.
   *
   * `getDoc` waits for the backend whenever `navigator.onLine` is true, so on a
   * captive portal or an unreachable backend it can hang for about ten seconds
   * before falling back to the cache. Callers on an interactive path (a POS
   * dialog, a register mutation) need a definite answer instead: this resolves
   * `undefined` when the document is absent, the read fails, or the deadline
   * passes, so the caller can carry on with what the cache told it.
   */
  async getDocFromServerAsync<T>(ref: DocPredicate<T>, timeoutMs = 5000): Promise<T | undefined> {
    if (!this.onlineOfflineService.isOnline) return undefined;
    await this.waitForAuthUser();

    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const snapshot = await Promise.race([
        getDocFromServer(this.doc(ref)),
        new Promise<undefined>(resolve => { timer = setTimeout(() => resolve(undefined), timeoutMs); })
      ]);
      return snapshot?.exists() ? snapshot.data() : undefined;
    } catch {
      return undefined;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  /**
   * Run a query without ever rejecting: an empty list stands for "could not be
   * answered" as well as "nothing matched". Waits for Firebase sign-in first,
   * so a query issued during the post-reload race is not refused with
   * `permission-denied`.
   */
  async getCollectionSafeAsync<T>(queryRef: Query<T>): Promise<T[]> {
    try {
      await this.waitForAuthUser();
      return await this.getCollectionAsync<T>(queryRef);
    } catch (error) {
      console.warn('[Firestore] query failed; treating it as no matches:', error);
      return [];
    }
  }

  /**
   * Run `work` while holding a named cross-tab lock, so every tab and window of
   * this browser serialises on it. This is what reproduces the Desktop's
   * per-machine lock around register open/close and counter increments.
   *
   * Where the Web Locks API is unavailable (older Safari, a non-secure origin)
   * the work simply runs — the caller's own per-document chain still serialises
   * it within this tab.
   */
  async withLock<T>(name: string, work: () => Promise<T>): Promise<T> {
    const locks = (navigator as any)?.locks;
    if (!locks?.request) return work();
    return locks.request(name, work) as Promise<T>;
  }

  /**
   * Issue a durable `setDoc` and hand back the server acknowledgement WITHOUT
   * awaiting it. The write lands in the persistent local cache at once and is
   * replayed by the SDK on reconnect (even across reloads), so the caller never
   * blocks on connectivity; it attaches to the returned promise only to learn
   * about a genuine rejection (permission, invalid data). A no-op handler is
   * registered here so an unobserved rejection never becomes an unhandled one.
   */
  queueSetDoc<T>(ref: DocPredicate<T>, data: Partial<T>, options: SetOptions = {}): Promise<void> {
    const documentRef = this.doc(ref);
    let acknowledged: Promise<void>;
    try {
      acknowledged = setDoc(documentRef, data as any, options);
    } catch (error) {
      acknowledged = Promise.reject(error);
    }
    acknowledged.catch(() => undefined);
    return acknowledged;
  }

  /** Sentinel that removes a field inside a merge write (`deleteField()`), exposed so callers stay mockable. */
  deleteFieldValue(): FieldValue {
    return deleteField();
  }

  async getDocValueAsync<T>(ref: DocPredicate<T>): Promise<T | undefined> {
    // Don't read before Firebase sign-in is established (avoids permission-denied on the reload race).
    await this.waitForAuthUser();
    const docSnapshot = await getDoc(this.doc(ref));
    const data = docSnapshot.data();
    return data;
  }

  docValueChanges$<T>(ref: DocPredicate<T>): Observable<T | undefined> {
    return docData<T | undefined>(this.doc(ref))
      .pipe(
        filter(data => data !== undefined),
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        takeUntil(this.loggedOut)
      );
  }

  col<T>(ref: CollectionPredicate<T>): CollectionReference<T> {
    return typeof ref === 'string' ? collection(this.angularFirestore, ref) as CollectionReference<T> : ref;
  }

  colSnapshots$<T>(queryRef: Query<T>): Observable<T[]> {
    return collectionChanges<T>(queryRef).pipe(map((docs: DocumentChange<T>[]) => {
      return docs.map(a => a.doc.data()) as T[];
    }))
      .pipe(distinctUntilChanged((prev, curr) => isEqual(prev, curr)), takeUntil(this.loggedOut));
  }

  async getCollectionWithIdsAsync<T>(queryRef: Query<T>): Promise<T[]> {
    return (await getDocs(queryRef)).docs.map(doc => {
      return { ...doc.data(), doc_id: doc.id };
    });
  }


  async getCollectionAsync<T>(queryRef: Query<T>): Promise<T[]> {
    return (await getDocs(queryRef)).docs.map(doc => {
      return doc.data();
    });
  }

  colValueChanges$<T>(queryRef: Query<T>): Observable<T[]> {
    return collectionData<T>(queryRef)
      .pipe(distinctUntilChanged((prev, curr) => isEqual(prev, curr)), takeUntil(this.loggedOut));
  }

  /**
   * Offline-first write settler.
   *
   * Firestore applies a write to the local IndexedDB cache immediately (latency
   * compensation: `onSnapshot` listeners fire right away and subsequent reads are
   * read-your-writes consistent), but the Promise returned by
   * `setDoc`/`updateDoc`/`deleteDoc`/`addDoc` only resolves once the BACKEND
   * acknowledges the write — per the Firebase docs it "won't resolve while you're
   * offline", and the backend synchronizes local changes automatically on
   * reconnect. Awaiting that promise on an interactive path therefore freezes the
   * UI while offline.
   *
   * This helper preserves online behaviour exactly while never blocking offline:
   *  - ONLINE  -> awaits the write verbatim, so every existing success/error path
   *               (permission-denied, validation, …) propagates to callers as before.
   *  - OFFLINE -> issues the write (the local cache + listeners update synchronously)
   *               and resolves immediately. The server-ack promise is kept alive so
   *               Firestore flushes it on reconnect; a genuine later rejection is
   *               logged instead of surfacing as an unhandled rejection.
   *
   * @param write   Thunk that performs the Firestore write and returns its promise.
   * @param opLabel Optional document path/label used for diagnostics.
   */
  async settleWrite(write: () => Promise<unknown>, opLabel?: string): Promise<void> {
    const serverAck = write();
    if (this.onlineOfflineService.isOnline) {
      await serverAck;
      return;
    }
    // Offline: the local-cache write is already durable; do not block the UI on
    // the server acknowledgement (which won't resolve until reconnect).
    serverAck.catch(err =>
      console.warn(`[Firestore] offline write deferred and later failed (${opLabel ?? 'unknown'}):`, err)
    );
  }

  async addDoc<T>(reference: CollectionReference<T>, data: T): Promise<DocumentReference<T>> {
    const isLoggedIn = await this.isLoggedIn();
    if (!isLoggedIn && this._customToken)
      await signInWithCustomToken(this.angularFireAuth, this._customToken);

    const newDocRef = doc(reference);
    await this.settleWrite(() => setDoc(newDocRef, data), newDocRef.path);
    return newDocRef;
  }

  async getCollectionWithDocIdAsync<T>(queryRef: Query<T>): Promise<T[]> {
    return (await getDocs(queryRef)).docs.map(doc => {
      return { ...doc.data(), docId: doc.id };
    });
  }

}
